<template>
  <SettingsPagePanel>
    <SettingsPagePanelHeader>
      <template #title v-if="!prevSetting">
        フリーワード自動応答設定 新規作成
      </template>
      <template #title v-else>フリーワード自動応答設定 編集</template>
      <template #right>
        <FlatButton @click="handleSubmit" :disabled="isSubmitDisabled">
          保存
        </FlatButton>
      </template>
    </SettingsPagePanelHeader>
    <div v-if="result">
      <NormalMessageChatReplyForm v-model="form" />
    </div>
  </SettingsPagePanel>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { onBeforeRouteLeave, useRouter } from 'vue-router'
import { captureException as newrelicCaptureException } from '@/newrelic'
import { useAlert } from '@/libs/alertPortal'
import { useLogger } from '@/logger/logger'

import FlatButton from '@/components/atoms/FlatButton.vue'
import SettingsPagePanel from '../SettingsPagePanel.vue'
import SettingsPagePanelHeader from '../SettingsPagePanelHeader.vue'

import type { SaveNormalMessageChatReplyPlannerSettingInput } from '@/types/schema.generated'
import {
  validateNormalMessageChatReplyPlannerSettingForm,
  type NormalMessageChatReplyPlannerSettingFormType,
} from './formSchemaForNormalMessage'
import NormalMessageChatReplyForm from './NormalMessageChatReplyForm.vue'
import { useNormalMessageChatReplyPlannerSettingQuery } from './NormalMessageChatReplyView.generated'
import { useSaveNormalMessageChatReplyPlannerSettingMutation } from './SettingsPageAutoChatReplyEditViewForNormalMessage.generated'

const emit = defineEmits<{
  (
    event: 'onBeforeLeaveConfirmation',
    callbacks: {
      onConfirm: () => void
      onAbort: () => void
    }
  ): void
}>()

const { result } = useNormalMessageChatReplyPlannerSettingQuery({
  fetchPolicy: 'no-cache',
})
const prevSetting = computed(
  () => result.value && result.value.normalMessageChatReplyPlannerSetting
)

const form = ref<NormalMessageChatReplyPlannerSettingFormType>({
  message: '',
  isEnabled: false,
})
watch(result, () => {
  if (!prevSetting.value) {
    return
  }

  form.value = {
    message: prevSetting.value.message,
    isEnabled: prevSetting.value.isEnabled,
  }
})

const isSubmitting = ref(false)
const isSubmitDisabled = computed(() => {
  return (
    !result.value ||
    !!validateNormalMessageChatReplyPlannerSettingForm(form.value) ||
    isSubmitting.value
  )
})

const { mutate: saveNormalMessageChatReplyPlannerSettingMutation } =
  useSaveNormalMessageChatReplyPlannerSettingMutation()

const alert = useAlert()
const logger = useLogger()
const router = useRouter()

const handleSubmit = async () => {
  isSubmitting.value = true

  const input: SaveNormalMessageChatReplyPlannerSettingInput = {
    message: form.value.message,
    isEnabled: form.value.isEnabled,
  }

  try {
    logger.ltlPeople.event.saveNormalMessageChatReplyPlannerSetting(
      logger.buildMeta(),
      {
        data: {
          message: input.message,
          isEnabled: input.isEnabled,
        },
      }
    )

    const payload = await saveNormalMessageChatReplyPlannerSettingMutation({
      input,
    })

    if (payload?.errors || !payload?.data) {
      throw new Error('saveNormalMessageChatReplyPlannerSettingMutation failed')
    }
  } catch (error) {
    newrelicCaptureException(error)
    alert.error('エラーが発生しました')
    isSubmitting.value = false
    return
  }

  alert.success('保存しました')
  // eslint-disable-next-line fp/no-mutating-methods
  router.push({ name: 'autoChatReplySetting' })
}

const isEdited = computed(() => {
  if (prevSetting.value) {
    return (
      form.value.message !== prevSetting.value.message ||
      form.value.isEnabled !== prevSetting.value.isEnabled
    )
  }

  return !prevSetting.value && form.value.message.length !== 0
})

onBeforeRouteLeave((_to, _from, next) => {
  if (!isEdited.value || isSubmitting.value) {
    next()
    return
  }

  emit('onBeforeLeaveConfirmation', {
    onConfirm: () => {
      next()
    },
    onAbort: () => next(false),
  })
})
</script>
